/* eslint-disable no-param-reassign */

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Autocomplete from "@mui/material/Autocomplete";
import moment from "moment";
import PropTypes from "prop-types";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useState, useCallback } from "react";
import DateRangePicker from "@mui/lab/MobileDateRangePicker";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";

// Material Dashboard 2 React example components
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import Footer from "components/Footer";
import DataTable from "components/Tables/DataTable";
import { toast } from "react-toastify";
import punchesTableConfig from "layouts/punches/data/punches";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";
import Loading from "components/Loading";
import { actions } from "../../store/reducers/punches";
import Modal from "./modal";

const punchesFromOptions = [
  {
    label: "All",
    id: "all",
  },
  {
    label: "Phone",
    id: "phone-call",
  },
  {
    label: "Qr Code",
    id: "qr-code",
  },
  {
    label: "Clock",
    id: "physical-clock",
  },
];
function Punches({ type }) {
  const [dateRange, setDateRange] = useState([moment().add(-1, "day").toDate(), moment().toDate()]);
  const [punchesFrom, setPunchesFrom] = useState("all");
  const { getRows, columns } = punchesTableConfig();
  const dispatch = useDispatch();

  const [qrCodeDetails, setShowPunchesDetails] = useState(null);

  const [open, setOpen] = useState(false);

  const handleClose = useCallback(() => {
    setOpen(false);
    setShowPunchesDetails(null);
  }, [setOpen, setShowPunchesDetails]);

  const {
    punches,
    // selectedRows,
    totalItems,
    page,
    perPage,
    qs,
    loading,
  } = useSelector((state) => state.punches);

  const fetchData = (device) => {
    if (dateRange && Array.isArray(dateRange) && dateRange.length === 2) {
      dispatch(
        actions.fetchPunchesRequest(type, page, perPage, dateRange, device, (err) =>
          toast.error(err)
        )
      );
    }
  };
  useEffect(() => {
    fetchData(punchesFrom);
  }, []);

  useEffect(() => {
    fetchData(punchesFrom);
  }, [page, perPage, qs]);

  const handleExport = () =>
    dispatch(
      actions.exportPunchesRequest(type, page, perPage, dateRange, punchesFrom, (err) =>
        toast.error(err)
      )
    );
  const rows = useMemo(
    () =>
      getRows(punches, (d) => {
        setOpen(true);
        setShowPunchesDetails(d);
      }),
    [punches, setShowPunchesDetails]
  );

  const selectedPunchesFrom = useMemo(() => {
    const lc = punchesFromOptions.find((p) => p.id === punchesFrom);
    if (lc) {
      return lc;
    }
    return null;
  }, [punchesFromOptions, punchesFrom]);
  return (
    <DashboardLayout>
      <DashboardNavbar />
      {loading && <Loading />}
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Punches
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                <MDBox pl={3}>
                  <MDButton
                    variant="gradient"
                    color="success"
                    size="medium"
                    disabled={totalItems === 0}
                    aria-label="close"
                    onClick={handleExport}
                  >
                    <Icon fontSize="medium">download</Icon> Export
                  </MDButton>
                </MDBox>
                <DataTable
                  entriesPerPage={{ defaultValue: perPage, entries: [5, 10, 15, 20, 50] }}
                  table={{ columns: columns.filter((c) => c.types.includes(type)), rows }}
                  isSorted={false}
                  extraFilters={
                    <>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DateRangePicker
                          startText="Start date"
                          endText="End date"
                          maxDate={moment().toDate()}
                          format="MM-DD-YYYY"
                          value={dateRange}
                          onAccept={() => {
                            fetchData(punchesFrom);
                          }}
                          style={{ marginRight: "1em" }}
                          onChange={(newValue) => setDateRange(newValue)}
                          renderInput={(startProps, endProps) => (
                            <>
                              <MDInput size="small" {...startProps} />
                              <MDBox sx={{ mx: 2 }}> to </MDBox>
                              <MDInput size="small" ield {...endProps} />
                            </>
                          )}
                        />
                      </LocalizationProvider>
                      <Autocomplete
                        sx={{ mx: 2 }}
                        style={{ width: 150 }}
                        value={selectedPunchesFrom}
                        options={punchesFromOptions}
                        getOptionLabel={(option) => (option ? option.label : "")}
                        onChange={(e, value) => {
                          setPunchesFrom(value?.id);
                          fetchData(value?.id);
                        }}
                        isOptionEqualToValue={(option, value) => option?.id === value}
                        renderInput={(params) => (
                          <MDInput
                            {...params}
                            label="Punches From"
                            name="punchesFrom"
                            variant="outlined"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      />
                    </>
                  }
                  showTotalEntries
                  noEndBorder
                  canSearch
                  totalCount={totalItems}
                  page={page}
                  perPage={perPage}
                  qs={qs}
                  setQs={(s) => dispatch(actions.setPunchesQs(s))}
                  gotoPage={(p) => dispatch(actions.setPunchesPage(p))}
                  setPageSize={(p) => dispatch(actions.setPunchesPerPage(p))}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <Modal open={!!(open || qrCodeDetails)} handleClose={handleClose} data={qrCodeDetails} />
    </DashboardLayout>
  );
}

Punches.defaultProps = {
  type: "employee",
};

Punches.propTypes = {
  type: PropTypes.string,
};

export default Punches;
